/**
 * Widget — spec §F2: "`Widget(title, icon, collapsible, draggable, onClose?)` — floating
 * map panel: `.mapwidget` + `.widget-head` (grab cursor, drag via `lib/draggable.ts`),
 * collapse '–/+' button."
 *
 * The markup and class names are the mockup's (`.mapwidget`, `.widget-head`,
 * `.collapsed`), so the legend, unit profile and indicator popover all reuse this shell.
 * `draggable` defaults to **true** (§F5.11: "every floating widget draggable within map
 * bounds"); the head then keeps its CSS grab cursor and `lib/draggable.ts` clamps the
 * widget inside `#mapwrap`. Initial position still comes from CSS — dragging only takes
 * over once the user grabs the header.
 */
import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useDraggable } from '../lib/draggable';
import { Icon, type IconName } from '../lib/icons';

export interface WidgetProps {
  id?: string;
  title: string;
  icon?: IconName;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** §F2/§F5.11: drag by the header, clamped inside the map container. */
  draggable?: boolean;
  onClose?: () => void;
  className?: string;
  style?: CSSProperties;
  /** Extra header controls, rendered between the title and the –/✕ buttons. */
  headExtra?: ReactNode;
  /**
   * Rendered *before* the title, where `icon` would go. §F5.9's popover leads with a domain
   * colour dot rather than a stroke icon, and a colour is not an `IconName`; anything that
   * identifies the panel rather than decorating it belongs in this slot.
   */
  headLead?: ReactNode;
  children: ReactNode;
}

export function Widget({
  id,
  title,
  icon,
  collapsible = true,
  defaultCollapsed = false,
  draggable = true,
  onClose,
  className,
  style,
  headExtra,
  headLead,
  children,
}: WidgetProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);

  useDraggable(widgetRef, headRef, { enabled: draggable });

  return (
    <div
      id={id}
      ref={widgetRef}
      className={`mapwidget${collapsed ? ' collapsed' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      <div
        className="widget-head"
        ref={headRef}
        style={draggable ? undefined : { cursor: 'default' }}
      >
        {headLead}
        {icon ? <Icon name={icon} size={15} /> : null}
        <span style={{ flex: 1 }}>{title}</span>
        {headExtra}
        {onClose ? (
          <button type="button" onClick={onClose} aria-label={t('widget.close')} title={t('widget.close')}>
            ×
          </button>
        ) : null}
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('widget.expand') : t('widget.collapse')}
            title={collapsed ? t('widget.expand') : t('widget.collapse')}
          >
            {collapsed ? '+' : '–'}
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export default Widget;
